import { createRootRoute, Outlet } from '@tanstack/react-router'
import { ThemeProvider } from '../components/theme-provider'
import { ToastProvider } from '../components/ui/toast-context'
import { AppErrorBoundary } from '@/components/error-boundary'
import { useCallback, useEffect } from 'react'

export const Route = createRootRoute({
  component: RootComponent,
})

function RootComponent() {
  // One-time migration of theme storage key before ThemeProvider initializes
  if (typeof window !== 'undefined') {
    try {
      const OLD_KEY = 'vite-ui-theme'
      const NEW_KEY = 'Sabhia-ui-theme'
      const oldVal = localStorage.getItem(OLD_KEY)
      const newVal = localStorage.getItem(NEW_KEY)
      if (oldVal && !newVal) {
        localStorage.setItem(NEW_KEY, oldVal)
        localStorage.removeItem(OLD_KEY)
      }
    } catch {}
  }

  // Ensure viewport meta is normalized (adds viewport-fit=cover without duplicates)
  useEffect(() => {
    const el = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null
    const defaultContent = 'width=device-width, initial-scale=1'
    const raw = el?.getAttribute('content') || defaultContent
    const parts = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)

    const map = new Map<string, string | true>()
    for (const p of parts) {
      const [k, v] = p.split('=')
      if (k) map.set(k.trim(), v ? v.trim() : true)
    }
    if (!map.has('viewport-fit')) map.set('viewport-fit', 'cover')

    const rebuilt = Array.from(map.entries())
      .map(([k, v]) => (v === true ? k : `${k}=${v}`))
      .join(', ')

    if (!el) {
      const meta = document.createElement('meta')
      meta.name = 'viewport'
      meta.content = rebuilt
      document.head.appendChild(meta)
    } else if (el.content !== rebuilt) {
      el.setAttribute('content', rebuilt)
    }
  }, [])

  // Skip link handler for consistent focus movement
  const onSkip = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const main = document.getElementById('main') as HTMLElement | null
    if (main) {
      main.focus()
      main.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  return (
    <ThemeProvider defaultTheme="system" storageKey="Sabhia-ui-theme">
      <ToastProvider>
        <AppErrorBoundary>
          <a
            href="#main"
            onClick={onSkip}
            className="sr-only focus:not-sr-only fixed left-2 top-2 z-[100] rounded bg-background px-3 py-2 text-foreground shadow focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Skip to content
          </a>

          <div id="app-root" className="min-h-screen overflow-x-hidden safe-areas">
            <main id="main" tabIndex={-1} className="outline-none">
              <Outlet />
            </main>
          </div>
        </AppErrorBoundary>
      </ToastProvider>
    </ThemeProvider>
  )
}